import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

if (environment.sentry?.dsn) {
  Sentry.init({
    dsn: environment.sentry.dsn,
    environment: environment.sentry.environment,
    tracesSampleRate: environment.sentry.tracesSampleRate,
    debug: environment.sentry.debug,

    // Send all PII data without filtering (headers, cookies, user data)
    sendDefaultPii: true,

    // Add initial tags to differentiate environments
    initialScope: {
      tags: {
        'app.environment': environment.sentry.environment,
        'app.version': environment.production ? 'production' : 'development',
        'app.base_url': environment.baseURL,
      },
    },

    // Filter and enhance events before sending
    beforeSend(event, hint) {
      // Add environment tag to all events for easy filtering
      event.tags = event.tags || {};
      event.tags['environment.type'] = environment.production ? 'production' : 'development';
      event.tags['server'] = environment.baseURL.includes('dev-') ? 'development' : 'production';

      // Keep all headers for debugging - do not scrub any data

      // Custom fingerprinting for common Angular errors to improve grouping
      if (hint.originalException) {
        const error = hint.originalException as any;
        const errorMessage = error?.message || '';

        // Group Angular lifecycle errors (ExpressionChangedAfterItHasBeenCheckedError)
        if (errorMessage.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
          event.fingerprint = ['angular-lifecycle', 'expression-changed'];
          event.tags['error_category'] = 'angular-lifecycle';
        }

        // Group null/undefined access errors by the property being accessed
        const nullAccessMatch = errorMessage.match(/Cannot read propert(?:y|ies) ['"]?(\w+)['"]? of (null|undefined)/);
        if (nullAccessMatch) {
          event.fingerprint = ['null-access', nullAccessMatch[1], nullAccessMatch[2]];
          event.tags['error_category'] = 'null-access';
          event.tags['property'] = nullAccessMatch[1];
        }

        // Group "Cannot set property" errors
        const setPropertyMatch = errorMessage.match(/Cannot set propert(?:y|ies) ['"]?(\w+)['"]? of (null|undefined)/);
        if (setPropertyMatch) {
          event.fingerprint = ['null-set', setPropertyMatch[1], setPropertyMatch[2]];
          event.tags['error_category'] = 'null-set';
          event.tags['property'] = setPropertyMatch[1];
        }

        // Group HttpErrorResponse by status code and endpoint
        if (error?.name === 'HttpErrorResponse' || errorMessage.includes('Http failure')) {
          const status = error?.status?.toString() || 'unknown';
          const url = error?.url || event.request?.url || 'unknown';
          try {
            const urlObj = new URL(url);
            event.fingerprint = ['http-error', status, urlObj.pathname];
            event.tags['error_category'] = 'http-error';
            event.tags['http_status'] = status;
          } catch (e) {
            event.fingerprint = ['http-error', status, 'unknown-url'];
          }
        }

        // Group timeout errors
        if (errorMessage.includes('Timeout') || errorMessage.includes('timeout')) {
          event.fingerprint = ['timeout-error', errorMessage.substring(0, 50)];
          event.tags['error_category'] = 'timeout';
        }

        // Group Angular router errors
        if (errorMessage.includes('NavigationError') || errorMessage.includes('Cannot match any routes')) {
          event.fingerprint = ['router-error', errorMessage.substring(0, 50)];
          event.tags['error_category'] = 'router';
        }

        // Group circular dependency errors
        if (errorMessage.includes('Circular dependency') || errorMessage.includes('cyclic dependency')) {
          event.fingerprint = ['circular-dependency', errorMessage.substring(0, 100)];
          event.tags['error_category'] = 'circular-dependency';
        }

        // Group zone.js errors
        if (errorMessage.includes('zone.js') || error?.stack?.includes('zone.js')) {
          event.fingerprint = ['zone-error', errorMessage.substring(0, 50)];
          event.tags['error_category'] = 'zone';
        }
      }

      return event;
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration({
        // Capture response bodies for failed requests (4xx, 5xx)
        failedRequestTargets: [/.*/],
      }),
      Sentry.replayIntegration({
        // Capture network details for ALL leetrans.com domains using regex
        networkDetailAllowUrls: [
          window.location.origin,
          /https:\/\/.*\.leetrans\.com\/.*/,  // Matches any subdomain of leetrans.com
          /https:\/\/leetrans\.com\/.*/,       // Matches root leetrans.com domain
        ],

        // Capture all common request headers
        networkRequestHeaders: [
          'Accept',
          'Accept-Encoding',
          'Accept-Language',
          'Authorization',
          'Cache-Control',
          'Connection',
          'Content-Length',
          'Content-Type',
          'Cookie',
          'Host',
          'Origin',
          'Pragma',
          'Referer',
          'User-Agent',
          'X-API-Key',
          'X-Request-ID',
          'X-Requested-With',
        ],

        // Capture all common response headers
        networkResponseHeaders: [
          'Access-Control-Allow-Credentials',
          'Access-Control-Allow-Headers',
          'Access-Control-Allow-Methods',
          'Access-Control-Allow-Origin',
          'Cache-Control',
          'Connection',
          'Content-Encoding',
          'Content-Length',
          'Content-Type',
          'Date',
          'ETag',
          'Expires',
          'Last-Modified',
          'Location',
          'Pragma',
          'Server',
          'Set-Cookie',
          'Vary',
          'WWW-Authenticate',
          'X-Request-ID',
          'X-Response-Time',
        ],

        // Capture request/response bodies for better debugging (including 500 errors)
        networkCaptureBodies: true,

        // Do not mask or filter any data
        maskAllInputs: false,
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.replayCanvasIntegration(),
    ],

    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Ignore common non-actionable errors
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Network errors that aren't actionable
      //^Non-Error promise rejection captured/i,
      // ResizeObserver errors (common and harmless)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Ignore errors from browser extensions
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });
}

if (environment.production) {
  enableProdMode();
}

platformBrowserDynamic()
  .bootstrapModule(AppModule)
  .catch((err) => console.error(err));
